import "@tento-ui/components/timer/timer-display";

import { Duration } from "@tento-chrono";
import { html, LonaWebComponent } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { Div } from "@tento-ui/component-builtin";
import { css, Style } from "@tento-ui/component-styles";
import { PomodoroTemplate } from "../template";
import { TimerDisplay } from "@tento-ui/components/timer/timer-display";
import { Timer } from "@tento-ui/components/timer/timer";

@component({
  name: "lona-timer-page",
})
export class LonaTimerPage extends LonaWebComponent {
  private $timerLabel: TimerDisplay = this.hydrate("timer-label");
  private timer: Option<Timer>;
  private unsubscribeTimer: Option<() => void>;

  onSizeChange: Option<(s: Size) => void> = (s) => {
    this.style.setProperty("--width", s.width + "px");
    this.style.setProperty("--height", s.height + "px");
  };

  static makeWith(props: LonaTimerPage.Props): LonaTimerPage {
    return LonaTimerPage.make().bind(props);
  }

  bind(props: LonaTimerPage.Props): this {
    this.style.setProperty("--page-bg", props.color);

    // Cleanup previous timer subscription before rebinding
    this.unsubscribeTimer?.();

    this.timer = props.timer;
    this.$timerLabel
      .bind({
        onDurationChange: props.onDurationChange,
      })
      .bindTimer(this.timer);

    this.unsubscribeTimer = this.timer.subscribe({
      onStateChange: (state) => {
        props.onStateChange?.(state);
        if (state.state === "finished") {
          props.onTimerComplete?.();
        }
      },
    });

    LonaTimerPage.DECORATIONS.forEach((deco, index) => {
      const $deco = Div.make();
      $deco.textContent = deco.emoji;
      $deco.classList.add("deco");
      $deco.assignStyles({
        "--start-x": deco.params.startX.toString(),
        "--start-y": deco.params.startY.toString(),
        "--vector-x": deco.params.vectorX.toString(),
        "--vector-y": deco.params.vectorY.toString(),
        "--start-rotation": deco.params.startRotation + "deg",
        "--end-rotation": deco.params.endRotation + "deg",
        "--start-scale": deco.params.startScale.toString(),
        "--end-scale": deco.params.endScale.toString(),
        "--start-blur": deco.params.startBlur + "px",
        "--end-blur": deco.params.endBlur + "px",
        "--delay": index * 0.1 + "s",
      });

      this.$("decorations").appendChild($deco);
    });
    return this;
  }

  bindProgress(progress: number) {
    this.style.setProperty("--progress", String(progress));
    this.$("dbg").textContent = progress + "";
  }

  bindState(state: Timer.SimpleState) {
    if (!this.timer) return;
    if (state === "running") return this.timer.start();
    if (state === "paused") return this.timer.pause();
    if (state === "not-started") return this.timer.reset();
  }

  reset() {
    this.timer?.reset();
  }

  bindRemainingTime(remaining: Duration.Time) {
    this.timer?.setRemainingTime(remaining);
  }

  getRemainingTime(): Option<Duration.Time> {
    return this.timer?.getRemainingTime();
  }

  getTimer(): Option<Timer> {
    return this.timer;
  }

  disconnectedCallback() {
    this.unsubscribeTimer?.();
  }

  static $styles: Style.Compat[] = [
    css`
      :host {
        height: 100%;
        width: 100%;
        padding: 0px !important;
        position: relative;
      }

      #root {
        background: var(--page-bg, var(--red));
        height: 100%;
        align-items: center;
        justify-content: center;
        border-radius: 12px;
        overflow: hidden;
      }

      #timer-label {
        --timer-font-size: 4rem;
        --timer-color: var(--text-pri);
        position: relative;
        z-index: 20;
      }

      #dbg {
        position: absolute;
        top: 50px;
        left: 50px;
        display: none;
      }

      #decorations {
        position: absolute;
        left: 0px;
        top: 0px;
        height: 100%;
        width: 100%;
        overflow: hidden;
        pointer-events: none;
      }

      .deco {
        position: absolute;
        font-size: 4rem;
        transition: transform 0.5s ease-out, opacity 0.3s ease;
        opacity: calc(var(--progress, 0) * 0.7 + 0.1);
        will-change: transform;

        /* Position at start point (corner) with proper centering */
        left: calc(var(--start-x) * var(--width, 100%));
        top: calc(var(--start-y) * var(--height, 100%));

        /* Center the decoration and apply all transforms */
        transform: translate(
          calc(-50% + var(--vector-x) * var(--width, 100px) * var(--progress, 0)),
          calc(-50% + var(--vector-y) * var(--height, 100px) * var(--progress, 0))
        )
        rotate(calc(
          var(--start-rotation) +
          (var(--end-rotation) - var(--start-rotation)) * var(--progress, 0)
        ))
        scale(calc(
          var(--start-scale) +
          (var(--end-scale) - var(--start-scale)) * var(--progress, 0)
        ));

        /* Apply blur for depth effect */
        filter: blur(calc(
          var(--start-blur) +
          (var(--end-blur) - var(--start-blur)) * var(--progress, 0)
        ));
      }

      #controls-container {
        position: absolute;
        left: 8px;
        bottom: 16px;
        width: calc(100% - 16px);
        z-index: 30;
      }
    `,
  ];
  static $html: Option<HTMLTemplateElement> = html`
    <std-col id=root>
      <std-timer-display id=timer-label></std-timer-display>
      <div id=dbg></div>
      <div id=decorations></div>
      <div id=controls-container>
        <slot name=controls></slot>
      </div>
    </std-col>
  `;
}

export namespace LonaTimerPage {
  export type Props = PomodoroTemplate & {
    timer: Timer;
    onDurationChange?: (duration: Duration.Time) => void;
    onStateChange?: (state: Timer.State) => void;
    onTimerComplete?: () => void;
  };

  export function parseFormattedTime(formattedTime: string): number {
    const [minutes, seconds] = formattedTime
      .split(":")
      .map((s) => Number.parseInt(s, 10));
    return (minutes * 60 + seconds) * 1000;
  }

  interface DecorationParams {
    startX: number;
    startY: number;
    vectorX: number;
    vectorY: number;
    startRotation: number;
    endRotation: number;
    startScale: number;
    endScale: number;
    startBlur: number;
    endBlur: number;
  }

  const createPerimeterDeco = (
    startX: number,
    startY: number,
    movementPx: number,
    directionX: number,
    directionY: number,
    rotationRange: [number, number],
    scaleRange: [number, number],
    blurRange: [number, number] = [0, 0],
  ): DecorationParams => {
    const relativeMovement = movementPx / 400;

    return {
      startX,
      startY,
      vectorX: directionX * relativeMovement,
      vectorY: directionY * relativeMovement,
      startRotation: 0,
      endRotation:
        rotationRange[0] +
        Math.random() * (rotationRange[1] - rotationRange[0]),
      startScale: scaleRange[0],
      endScale: scaleRange[1],
      startBlur: blurRange[0],
      endBlur: blurRange[1],
    };
  };

  // biome-ignore format: format
  export const DECORATIONS: {
    emoji: string,
    params: DecorationParams
  }[] = [
    // Top edge decorations - mix of sharp and blurred
    {
      emoji: "🎉",
      params: createPerimeterDeco(
        -0.05,
        -0.1,
        50,
        0.5,
        1,
        [-50, 60],
        [0.3, 1.2],
        [3, 0],
      ),
    },
    {
      emoji: "✨",
      params: createPerimeterDeco(
        0.3,
        -0.08,
        50,
        0.2,
        1,
        [-50, 60],
        [0.4, 0.9],
        [5, 0],
      ),
    },
    {
      emoji: "🌟",
      params: createPerimeterDeco(
        0.7,
        -0.12,
        50,
        -0.3,
        1,
        [-50, 60],
        [0.2, 1.1],
        [2, 0],
      ),
    },
    {
      emoji: "💫",
      params: createPerimeterDeco(
        1.05,
        -0.06,
        50,
        -0.8,
        0.6,
        [-50, 60],
        [0.35, 1.0],
        [6, 3],
      ),
    },

    // Right edge decorations - background elements with more blur
    {
      emoji: "🎊",
      params: createPerimeterDeco(
        1.08,
        0.25,
        50,
        -1,
        0.4,
        [-50, 60],
        [0.25, 1.3],
        [0, 0],
      ),
    },
    {
      emoji: "🎈",
      params: createPerimeterDeco(
        1.12,
        0.75,
        50,
        -1,
        -0.2,
        [-50, 60],
        [0.3, 0.8],
        [6, 2],
      ),
    },

    // Bottom edge decorations - foreground elements, sharp
    {
      emoji: "⭐",
      params: createPerimeterDeco(
        0.8,
        1.1,
        50,
        -0.4,
        -1,
        [-50, 60],
        [0.4, 1.15],
        [0, 0],
      ),
    },
    {
      emoji: "🌈",
      params: createPerimeterDeco(
        0.2,
        1.06,
        50,
        0.3,
        -1,
        [-50, 60],
        [0.2, 1.0],
        [7, 1],
      ),
    },

    // Left edge decorations - varied depths
    {
      emoji: "🔥",
      params: createPerimeterDeco(
        -0.08,
        0.6,
        50,
        1,
        -0.4,
        [-50, 60],
        [0.3, 1.25],
        [0, 0],
      ),
    },
    {
      emoji: "💎",
      params: createPerimeterDeco(
        -0.1,
        0.3,
        50,
        1,
        0.5,
        [-50, 60],
        [0.35, 0.9],
        [6, 0],
      ),
    },
  ];
}
